import React, { FC, useState, useRef, useEffect } from "react";
import {
    StyleSheet,
    Dimensions,
    View,
    Text,
    TouchableOpacity,
    SafeAreaView,
    Platform,
} from "react-native";
import { Camera, CameraType, FlashMode, VideoStabilization } from "expo-camera";
import { Video, Audio } from "expo-av";
import { MaterialIcons, Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import { PinchGestureHandler, GestureHandlerRootView } from 'react-native-gesture-handler';
import { useKeepAwake } from 'expo-keep-awake'; // NUEVO: Pantalla siempre encendida
import * as Brightness from 'expo-brightness'; // NUEVO: Ahorro de batería
import { formatDateLocal } from "../../utils/general";

const WINDOW_HEIGHT = Dimensions.get("window").height;

const closeButtonSize = Math.floor(WINDOW_HEIGHT * 0.032);
const captureSize = Math.floor(WINDOW_HEIGHT * 0.09);


export interface ICameraUIProps {
    onCallback: Function, onRequestClose: Function, forVideo?: boolean, bodyCam?: boolean, mute?: boolean
}


export const CameraUI: FC<ICameraUIProps> = (({ ...props }) => {
    const [hasPermission, setHasPermission] = useState(null);
    const [cameraType, setCameraType] = useState(CameraType.back);
    const [flashMode, setFlashMode] = useState(FlashMode.off);
    const [zoom, setZoom] = useState(0);
    const [isMuted, setIsMuted] = useState(props.mute || false);

    const [isPreview, setIsPreview] = useState(false);
    const [isCameraReady, setIsCameraReady] = useState(false);
    const [isVideoRecording, setIsVideoRecording] = useState(false);
    const [videoSource, setVideoSource] = useState(null);
    const [elapsed, setElapsed] = React.useState<number>(0);
    const [started, setStarted] = React.useState<number>(0);
    const [videoQuality, setVideoQuality] = useState<'480p' | '720p'>('480p');
    const [isTakingPicture, setIsTakingPicture] = useState(false);
    const [isStartingRecording, setIsStartingRecording] = useState(false);
    const [isStoppingRecording, setIsStoppingRecording] = useState(false);
    const cameraRef = useRef();
    const bodyCam = props.bodyCam || false;
    const muted = props.mute || false;
    const stopRequest = useRef(false);
    let _recordingTimer = null;

    const fragmentDuration = bodyCam ? 150 : 60;

    const qualityProfiles = {
        '480p': {
            label: 'SD',
            bitrate: 2 * 1000 * 1000,
            expoQuality: Camera.Constants.VideoQuality["480p"],
            color: "#22c55e", // Color verde para modo seguro
            photoWidth: 1280
        },
        '720p': {
            label: 'HD',
            bitrate: 6 * 1000 * 1000,
            expoQuality: Camera.Constants.VideoQuality["720p"],
            color: "#ef4444",
            photoWidth: 1920
        }
    };


    useKeepAwake();
    const playBeep = async () => {
        try {
            const { sound } = await Audio.Sound.createAsync(require('../../../assets/beep.mp3'));
            await sound.playAsync();
            // Liberar memoria tras sonar
            sound.setOnPlaybackStatusUpdate((status) => {
                sound.unloadAsync();
            });
        } catch (e) { console.log("Error sonido", e); }
    };

    useEffect(() => {
        (async () => {
            try {
                const { status } = await Camera.getCameraPermissionsAsync();
                console.log(status)
                if (status !== "granted")
                    await Camera.requestCameraPermissionsAsync();
                console.log("Necesito CAMARA")
                setIsCameraReady(true);
                if (props.forVideo) await Camera.getMicrophonePermissionsAsync();
                setHasPermission(status === "granted");
            } catch (e) {
                console.log(e);
                if (Platform.OS == "web") {
                    await Camera.requestCameraPermissionsAsync();
                    setHasPermission(true);
                }

            }
        })();
    }, []);

    const onCameraReady = () => {
        setIsCameraReady(true);
    };

    const takePicture = async () => {
        if (isTakingPicture) return;
        if (cameraRef.current) {
            setIsTakingPicture(true);
            console.log("SACANDO FOTO")
            try {
                const options = {
                    quality: 0.7, base64: Platform.OS == "web" ? true : false, skipProcessing: false, orientation: "auto",
                    
                };
                //@ts-ignore
                const data = await cameraRef.current.takePictureAsync(options);
                data.targetWidth = qualityProfiles[videoQuality].photoWidth;
                const source = data.uri;
                console.log("SACANDO FOTO SACADA")

                if (source) {
                    props.onRequestClose();
                    props.onCallback(data);
                }
            } catch (e) {
                console.warn(e);
            } finally {
                setIsTakingPicture(false);
            }
        }
    };
    const changeZoom = (event) => {
        // Extraemos el scale sincrónica y directamente del evento
        const currentScale = event.nativeEvent.scale;
        
        setZoom(prevZoom => {
            let newZoom = prevZoom;
            
            if (currentScale > 1) {
                newZoom += 0.1;
            } else if (currentScale < 1) {
                newZoom -= 0.1;
            }
            return Math.min(Math.max(newZoom, 0), 1);
        });
    };

    const recordVideo = async () => {
        if (!cameraRef.current || isStartingRecording || isVideoRecording) return;
        setIsStartingRecording(true);

        setIsVideoRecording(true);
        stopRequest.current = false;
        setStarted(new Date().getTime());

        // Si es BodyCam, bajamos el brillo al iniciar
        if (bodyCam) {
            await Brightness.setBrightnessAsync(0.5);
        }

        setIsStartingRecording(false);
        await runRecordingLoop();
    };
    const runRecordingLoop = async () => {
        if (stopRequest.current || !cameraRef.current) return;

        try {
            const options = {
                quality: qualityProfiles[videoQuality].expoQuality,
                maxDuration: fragmentDuration,
                videoBitrate: qualityProfiles[videoQuality].bitrate,
                VideoStabilization: VideoStabilization.off,
                mute: isMuted
            };
            const fechaActual = formatDateLocal(new Date().getTime());
            const data = await cameraRef.current.recordAsync(options);
            data.name = fechaActual.concat(".mp4");
            if (bodyCam && !stopRequest.current) {
                console.log("Reiniciando fragmento automáticamente...");
                runRecordingLoop();
                props.onCallback(data);
            } else {
                console.log("Cerrando Video....")
                props.onCallback(data);
                terminateSession();
            }
        } catch (error) {
            console.warn(error);
            terminateSession();
        }
    };

    const terminateSession = async () => {
        setIsVideoRecording(false);
        setIsStoppingRecording(false);
        if (bodyCam) {
            await Brightness.restoreSystemBrightnessAsync();
        }
        props.onRequestClose(true);
    };



    React.useEffect(() => {
        if (isVideoRecording && _recordingTimer == null) {
            _recordingTimer = setInterval(() => {
                setElapsed(((new Date().getTime() - started) / 1000));
            }, 1000);
        }
    }, [isVideoRecording]);

    const stopVideoRecording = () => {
        if (isStoppingRecording || stopRequest.current) return;
        setIsStoppingRecording(true);
        console.log("stopRecording")
        stopRequest.current = true;
        if (cameraRef.current) {
            // @ts-ignore
            cameraRef.current.stopRecording();
        }
    };

    const switchCamera = () => {
        if (isPreview) {
            return;
        }
        setCameraType((prevCameraType) =>
            prevCameraType === CameraType.back
                ? CameraType.front
                : CameraType.back
        );
    };

    const cancelPreview = async () => {
        // @ts-ignore
        await cameraRef.current.resumePreview();
        setIsPreview(false);
        setVideoSource(null);

    };

    const renderCancelPreviewButton = () => (
        <TouchableOpacity onPress={cancelPreview} style={styles.closeButton}>
            <View style={[styles.closeCross, { transform: [{ rotate: "45deg" }] }]} />
            <View
                style={[styles.closeCross, { transform: [{ rotate: "-45deg" }] }]}
            />
        </TouchableOpacity>
    );

    const renderVideoPlayer = () => (
        <Video
            source={{ uri: videoSource }}
            shouldPlay={true}
            style={styles.media}
        />
    );

    const renderVideoRecordIndicator = () => (
        <View style={styles.recordIndicatorContainer}>
            <MaterialCommunityIcons name="record-rec" size={24} color="#f87171" />
            <Text style={styles.recordTitle}>{`Grabando ${elapsed.toFixed(0)}'`}</Text>
        </View>
    );

    const renderCaptureControl = () => (
        <View style={styles.control}>
            {!props.forVideo ? (
                <TouchableOpacity
                    onPress={() => { setFlashMode(flashMode === FlashMode.off ? FlashMode.on : FlashMode.off); }}
                    style={styles.iconButton}
                >
                    <Ionicons
                        name={flashMode === FlashMode.off ? "md-flash-off" : 'md-flash'}
                        size={28}
                        color="white"
                    />
                </TouchableOpacity>
            ) : (
                <TouchableOpacity
                    onPress={() => { setFlashMode(flashMode === FlashMode.off ? FlashMode.torch : FlashMode.off); }}
                    style={styles.iconButton}
                >
                    <Ionicons
                        name="md-flashlight-outline"
                        size={28}
                        color={flashMode === FlashMode.off ? "white" : '#facc15'}
                    />
                </TouchableOpacity>
            )}

            {props.forVideo && !isVideoRecording && (
                <TouchableOpacity
                    disabled={props.mute}
                    onPress={() => { setIsMuted(!isMuted); }}
                    style={[styles.iconButton, props.mute && { opacity: 0.5 }]}
                >
                    <Ionicons
                        name={isMuted ? "mic-off-outline" : "mic-outline"}
                        size={28}
                        color={isMuted ? '#facc15' : "white"}
                    />
                </TouchableOpacity>
            )}

            {!props.forVideo && (
                <TouchableOpacity
                    disabled={isTakingPicture}
                    onPress={takePicture}
                    style={styles.iconButton}
                >
                    <MaterialIcons
                        name="camera"
                        size={64}
                        color={isTakingPicture ? "#9ca3af" : "white"}
                    />
                </TouchableOpacity>
            )}

            {props.forVideo && !isVideoRecording && (
                <TouchableOpacity
                    disabled={isStartingRecording}
                    onPress={recordVideo}
                    style={styles.iconButton}
                >
                    <Ionicons
                        name="radio-button-on-sharp"
                        size={64}
                        color={isStartingRecording ? "#9ca3af" : "#f87171"}
                    />
                </TouchableOpacity>
            )}

            {props.forVideo && isVideoRecording && (
                <TouchableOpacity
                    disabled={isStoppingRecording}
                    onPress={stopVideoRecording}
                    style={styles.iconButton}
                >
                    <Ionicons
                        name="stop-circle-outline"
                        size={64}
                        color={isStoppingRecording ? "#9ca3af" : "#f87171"}
                    />
                </TouchableOpacity>
            )}

            <TouchableOpacity
                disabled={isVideoRecording}
                onPress={switchCamera}
                style={[styles.iconButton, isVideoRecording && { opacity: 0.5 }]}
            >
                <Ionicons
                    name="camera-reverse"
                    size={28}
                    color="white"
                />
            </TouchableOpacity>
        </View>
    );

    



    return (
        <GestureHandlerRootView style={styles.container}>
            <PinchGestureHandler onGestureEvent={(event) => changeZoom(event)}>
                <View style={styles.container}>
                <Camera
                    ref={cameraRef}
                    style={styles.container}
                    type={cameraType}
                    useCamera2Api={false}
                    ratio="16:9"
                    zoom={zoom}
                    flashMode={flashMode}
                    onCameraReady={onCameraReady}
                    onMountError={(error) => {
                        console.log("camera error", error);
                    }}
                />
                <TouchableOpacity
                    style={styles.closeButton}
                    disabled={isVideoRecording}
                    onPress={() => setVideoQuality(videoQuality === '480p' ? '720p' : '480p')}
                >
                    <MaterialCommunityIcons
                        name={videoQuality === '720p' ? "high-definition" : "standard-definition"}
                        size={24}
                        color={isVideoRecording ? "#6b7280" : qualityProfiles[videoQuality].color}
                    />
                </TouchableOpacity>
                <View style={styles.container} pointerEvents="box-none">
                    {isVideoRecording && renderVideoRecordIndicator()}
                    {videoSource && renderVideoPlayer()}
                    {isPreview && renderCancelPreviewButton()}
                    {!videoSource && !isPreview && renderCaptureControl()}
                </View>
            </View>
        </PinchGestureHandler>
        </GestureHandlerRootView>
    );
});

const styles = StyleSheet.create({
    container: {
        ...StyleSheet.absoluteFillObject,
    },
    closeButton: {
        position: "absolute",
        top: 35,
        right: 15,
        height: closeButtonSize,
        width: closeButtonSize,
        borderRadius: Math.floor(closeButtonSize / 2),
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#FFFFFF",
        opacity: 0.7,
        zIndex: 2,
    },
    media: {
        ...StyleSheet.absoluteFillObject,
    },
    closeCross: {
        width: "68%",
        height: 1,
        backgroundColor: "black",
    },
    control: {
        position: "absolute",
        flexDirection: "row",
        bottom: 10,
        width: "100%",
        alignItems: "center",
        justifyContent: "space-around",
    },
    capture: {
        backgroundColor: "#f5f6f5",
        borderRadius: 5,
        height: captureSize,
        width: captureSize,
        marginHorizontal: 31,
    },
    recordIndicatorContainer: {
        flexDirection: "row",
        position: "absolute",
        top: 25,
        alignSelf: "center",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "transparent",
        opacity: 0.7,
    },
    recordTitle: {
        fontSize: 14,
        color: "#ffffff",
        textAlign: "center",
    },
    recordDot: {
        borderRadius: 3,
        height: 6,
        width: 6,
        backgroundColor: "#ff0000",
        marginHorizontal: 5,
    },
    text: {
        color: "#fff",
    },
    iconButton: {
        justifyContent: "center",
        alignItems: "center",
        padding: 6,
    },
});